/**
 * Red de vínculos
 * -----------------------------------------------------------------------------
 * Personas en burbujas, unidas por lo que las une, sin escribir JavaScript en la
 * aplicación:
 *
 *   <div class="company-red" data-company-red
 *        data-company-red-nodos='[{"id":1,"nombre":"Paola","nota":"224.385","url":"/clientes/2","marcado":true}]'
 *        data-company-red-enlaces='[{"de":1,"a":2,"etiqueta":"hermana","fuerte":true}]'>
 *   </div>
 *
 * ## Por qué una lista no basta
 *
 * Una tabla de parentescos enseña pares: «Cristian y Paola», «Paola y
 * Guacamayo». Son dos filas y **una sola casa de tres**, y eso en una tabla no
 * se ve por mucho que se ordene. Puesto en burbujas se ve de un vistazo, que es
 * justo lo que hay que decidir antes de prestarle a un cuarto de ahí.
 *
 * ## Sin librería de grafos
 *
 * Las buenas pesan más que todo el tema junto y traen un modelo de datos propio.
 * Aquí las posiciones salen de un cálculo de veinte líneas —los nodos se
 * repelen, los enlaces tiran— que se ejecuta unas cuantas vueltas y se detiene.
 * No hay animación en bucle: una red que sigue moviéndose sola es imposible de
 * leer y calienta el portátil de quien la deja abierta.
 *
 * ## Sale siempre igual
 *
 * Las posiciones de partida se reparten en círculo por el orden en que llegan
 * los nodos, **sin azar**. Con `Math.random()` la misma familia salía distinta
 * en cada recarga, y una foto que cambia sola no se puede comparar con la de
 * ayer ni señalar por teléfono.
 *
 * ## Se puede arrastrar
 *
 * Con muchas personas siempre queda algún cruce. Arrastrar una burbuja la
 * despega y las demás se quedan donde estaban: no se recalcula nada, porque
 * recalcular movería justo lo que quien arrastra estaba mirando.
 *
 * ## El tamaño no lo pone el dibujo
 *
 * Una burbuja mide lo mismo con tres personas que con treinta. Estirando el
 * dibujo hasta llenar el hueco, una familia de tres salía con burbujas enormes y
 * una de veinte con letra ilegible: el mismo componente parecía dos cosas
 * distintas según a quién se mirara.
 *
 * Se arranca a tamaño natural y **se aleja solo lo que haga falta** para que
 * quepa. A partir de ahí manda quien mira: la rueda acerca y aleja, y arrastrar
 * el fondo mueve el lienzo. El botón de ajustar devuelve la vista de entrada,
 * que es lo que hace falta después de perderse.
 */

const ATTR = 'data-company-red';

/** Radio de una burbuja, en píxeles a tamaño natural. */
const RADIO = 26;

/** Hasta dónde se puede acercar y alejar con la rueda o los botones. */
const ZOOM_MINIMO = 0.2;
const ZOOM_MAXIMO = 2.5;

/** Lo que cambia el zoom con cada golpe de rueda o clic en los botones. */
const PASO_DE_ZOOM = 1.2;

/** Vueltas del cálculo de posiciones. Con esto una familia de veinte ya no se cruza. */
const VUELTAS = 320;

/** Lo que se quieren separar dos personas que no tienen nada que ver. */
const REPULSION = 42000;

/** Lo que mide un enlace cuando nada tira de él. */
const LARGO = 150;

/** Una variable CSS del tema, con un color de reserva si no está definida. */
function color(nombre, reserva) {
  const valor = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim();

  return valor === '' ? reserva : valor;
}

/** Lo que venga en un atributo como JSON, o el valor de reserva. */
function json(elemento, sufijo, reserva) {
  const bruto = elemento.getAttribute(`${ATTR}-${sufijo}`);

  if (!bruto) return reserva;

  try {
    return JSON.parse(bruto);
  } catch (error) {
    // Un atributo mal escrito no puede tumbar la página: la red se queda vacía
    // y lo dice, que es lo mismo que hace cuando de verdad no hay nada.
    return reserva;
  }
}

function crear(etiqueta, atributos = {}) {
  const nodo = document.createElementNS('http://www.w3.org/2000/svg', etiqueta);

  Object.entries(atributos).forEach(([nombre, valor]) => {
    if (valor !== null && valor !== undefined) nodo.setAttribute(nombre, String(valor));
  });

  return nodo;
}

/**
 * Cuántas redes van montadas. Los recortes de las fotos se referencian por
 * nombre, y dos redes en la misma página con los mismos nombres compartirían
 * los del primero: todas las caras saldrían recortadas por la burbuja de otro.
 */
let montadas = 0;

export class Red {
  constructor(elemento) {
    this.id = (montadas += 1);
    this.elemento = elemento;
    this.nodos = json(elemento, 'nodos', []).map((nodo, indice) => ({ ...nodo, indice }));
    this.enlaces = json(elemento, 'enlaces', []);

    elemento.__companyRed = this;

    this.pintar();
  }

  pintar() {
    this.elemento.textContent = '';

    if (this.nodos.length === 0) {
      const vacia = document.createElement('p');

      vacia.className = 'company-red-vacia text-muted';
      vacia.textContent = this.elemento.getAttribute(`${ATTR}-vacia`) || 'Todavía no hay nada que enlazar.';

      this.elemento.appendChild(vacia);

      return;
    }

    this.colocar();

    this.svg = crear('svg', {
      class: 'company-red-lienzo',
      role: 'img',
    });

    // Todo el dibujo cuelga de un grupo con una transformación. Mover y acercar
    // es cambiar esa transformación, así que ni las posiciones ni los tamaños se
    // recalculan al hacerlo: la red de veinte personas se arrastra igual de
    // suave que la de tres.
    this.lienzo = crear('g', { class: 'company-red-vista' });
    this.capaEnlaces = crear('g', { class: 'company-red-enlaces' });
    this.capaNodos = crear('g', { class: 'company-red-nodos' });

    this.lienzo.appendChild(this.capaEnlaces);
    this.lienzo.appendChild(this.capaNodos);
    this.svg.appendChild(this.lienzo);

    this.enlaces.forEach((enlace) => this.pintarEnlace(enlace));
    this.nodos.forEach((nodo) => this.pintarNodo(nodo));

    this.elemento.appendChild(this.svg);
    this.elemento.appendChild(this.mandos());

    this.vista = { x: 0, y: 0, escala: 1 };

    // Nadie ha tocado la vista todavía, así que se puede recolocar sola.
    this.tocada = false;

    this.medir();
    this.permitirArrastre();

    // El hueco cambia varias veces antes de quedarse quieto: al montar todavía
    // no tiene ancho, luego llega el menú lateral, luego se pliega, y en el
    // teléfono al girarlo. **Sin volver a medir, la red se colocaba para el
    // ancho de aquel primer instante —cero— y salía del tamaño de una mosca en
    // una esquina.**
    if (typeof ResizeObserver === 'function') {
      this.observador = new ResizeObserver(() => this.medir());
      this.observador.observe(this.elemento);
    }
  }

  /**
   * El lienzo mide lo que mide el hueco, en píxeles de pantalla.
   *
   * Y de paso recoloca la red, mientras nadie la haya movido a mano. Después de
   * que alguien acerque o arrastre no se toca: recolocarla porque el menú
   * lateral se plegó le desharía lo que acababa de encuadrar.
   */
  medir() {
    const caja = this.elemento.getBoundingClientRect();

    this.ancho = Math.max(1, caja.width);
    this.alto = Math.max(1, caja.height);

    this.svg.setAttribute('viewBox', `0 0 ${this.ancho} ${this.alto}`);

    if (!this.tocada) this.ajustar();
  }

  /** Los botones de acercar, alejar y volver a la vista de entrada. */
  mandos() {
    const caja = document.createElement('div');

    caja.className = 'company-red-mandos';
    caja.innerHTML = [
      '<button type="button" class="btn btn-sm btn-light" data-red-acercar aria-label="Acercar"><i class="fas fa-plus"></i></button>',
      '<button type="button" class="btn btn-sm btn-light" data-red-alejar aria-label="Alejar"><i class="fas fa-minus"></i></button>',
      '<button type="button" class="btn btn-sm btn-light" data-red-ajustar aria-label="Ajustar a la pantalla"><i class="fas fa-compress"></i></button>',
    ].join('');

    caja.querySelector('[data-red-acercar]').addEventListener('click', () => this.acercar(PASO_DE_ZOOM));
    caja.querySelector('[data-red-alejar]').addEventListener('click', () => this.acercar(1 / PASO_DE_ZOOM));

    // Ajustar a mano devuelve la red al cuidado de la casa: si el hueco cambia
    // después, se vuelve a encuadrar sola. Es lo que se pide con ese botón.
    caja.querySelector('[data-red-ajustar]').addEventListener('click', () => {
      this.tocada = false;
      this.ajustar();
    });

    return caja;
  }

  /**
   * La vista de entrada: centrada y a tamaño natural, salvo que no quepa.
   *
   * Nunca se agranda por encima de 1. Estirar una familia de tres hasta llenar
   * el panel daba burbujas del tamaño de un puño, y el mismo componente parecía
   * otro según a quién se estuviera mirando.
   */
  ajustar() {
    const caja = this.limites();
    const escala = Math.min(1, this.ancho / caja.ancho, this.alto / caja.alto);

    this.vista = {
      escala,
      x: this.ancho / 2 - (caja.x + caja.ancho / 2) * escala,
      y: this.alto / 2 - (caja.y + caja.alto / 2) * escala,
    };

    this.aplicarVista();
  }

  /** Acerca o aleja alrededor de un punto de la pantalla —el cursor, o el centro—. */
  acercar(factor, haciaX = this.ancho / 2, haciaY = this.alto / 2) {
    const antes = this.vista.escala;
    const escala = Math.min(ZOOM_MAXIMO, Math.max(ZOOM_MINIMO, antes * factor));

    // Desde aquí manda quien mira, aunque el tope no deje acercar más.
    this.tocada = true;

    if (escala === antes) return;

    // El punto que estaba bajo el cursor sigue estando bajo el cursor. Sin esto
    // la rueda acerca hacia una esquina y hay que ir persiguiendo el dibujo.
    this.vista = {
      escala,
      x: haciaX - ((haciaX - this.vista.x) * escala) / antes,
      y: haciaY - ((haciaY - this.vista.y) * escala) / antes,
    };

    this.aplicarVista();
  }

  aplicarVista() {
    this.lienzo.setAttribute(
      'transform',
      `translate(${this.vista.x} ${this.vista.y}) scale(${this.vista.escala})`,
    );
  }

  /** De un punto de la pantalla al sistema del dibujo. */
  enElDibujo(evento) {
    const caja = this.svg.getBoundingClientRect();
    const x = evento.clientX - caja.left;
    const y = evento.clientY - caja.top;

    return {
      pantallaX: x,
      pantallaY: y,
      x: (x - this.vista.x) / this.vista.escala,
      y: (y - this.vista.y) / this.vista.escala,
    };
  }

  /**
   * Dónde va cada burbuja.
   *
   * Se empieza repartiéndolas en círculo por su orden y se dejan correr unas
   * vueltas: cada par se empuja y cada enlace tira. Al final, quien está unido
   * queda cerca y quien no, lejos, que es toda la lectura que hace falta.
   */
  colocar() {
    const cuantos = this.nodos.length;
    const radio = Math.max(LARGO, (cuantos * LARGO) / (2 * Math.PI));

    this.nodos.forEach((nodo, i) => {
      const angulo = (2 * Math.PI * i) / cuantos;

      nodo.x = Math.cos(angulo) * radio;
      nodo.y = Math.sin(angulo) * radio;
    });

    const porId = new Map(this.nodos.map((nodo) => [String(nodo.id), nodo]));
    const tirantes = this.enlaces
      .map((enlace) => [porId.get(String(enlace.de)), porId.get(String(enlace.a))])
      .filter(([uno, otro]) => uno && otro && uno !== otro);

    for (let vuelta = 0; vuelta < VUELTAS; vuelta += 1) {
      // Se enfría: al principio se mueven mucho para deshacer los cruces y al
      // final apenas, para que el dibujo se asiente en vez de vibrar.
      const paso = 1 - vuelta / VUELTAS;

      this.nodos.forEach((nodo) => {
        nodo.dx = 0;
        nodo.dy = 0;
      });

      for (let i = 0; i < cuantos; i += 1) {
        for (let j = i + 1; j < cuantos; j += 1) {
          const uno = this.nodos[i];
          const otro = this.nodos[j];

          let x = otro.x - uno.x;
          let y = otro.y - uno.y;
          let distancia = Math.sqrt(x * x + y * y);

          // Dos exactamente encima: se separan por su orden y no al azar, para
          // que la red no cambie de forma entre una recarga y la siguiente.
          if (distancia < 0.01) {
            x = (j - i) * 0.5;
            y = (i + 1) * 0.5;
            distancia = Math.sqrt(x * x + y * y);
          }

          const empuje = REPULSION / (distancia * distancia);

          uno.dx -= (x / distancia) * empuje;
          uno.dy -= (y / distancia) * empuje;
          otro.dx += (x / distancia) * empuje;
          otro.dy += (y / distancia) * empuje;
        }
      }

      tirantes.forEach(([uno, otro]) => {
        const x = otro.x - uno.x;
        const y = otro.y - uno.y;
        const distancia = Math.max(0.01, Math.sqrt(x * x + y * y));
        const tiron = (distancia - LARGO) * 0.05;

        uno.dx += (x / distancia) * tiron;
        uno.dy += (y / distancia) * tiron;
        otro.dx -= (x / distancia) * tiron;
        otro.dy -= (y / distancia) * tiron;
      });

      this.nodos.forEach((nodo) => {
        // Un tope por vuelta: sin él, dos burbujas que arrancan pegadas salen
        // disparadas a la otra punta y el dibujo ya no se recupera.
        const tope = 30 * paso;
        const largo = Math.sqrt(nodo.dx * nodo.dx + nodo.dy * nodo.dy) || 1;
        const factor = Math.min(1, tope / largo);

        nodo.x += nodo.dx * factor;
        nodo.y += nodo.dy * factor;
      });
    }
  }

  /** La caja que las contiene a todas, con aire para el rótulo de debajo. */
  limites() {
    const margen = RADIO + 46;
    const xs = this.nodos.map((nodo) => nodo.x);
    const ys = this.nodos.map((nodo) => nodo.y);

    const x = Math.min(...xs) - margen;
    const y = Math.min(...ys) - margen;

    return {
      x,
      y,
      ancho: Math.max(...xs) + margen - x,
      alto: Math.max(...ys) + margen - y,
    };
  }

  pintarEnlace(enlace) {
    const uno = this.nodos.find((nodo) => String(nodo.id) === String(enlace.de));
    const otro = this.nodos.find((nodo) => String(nodo.id) === String(enlace.a));

    if (!uno || !otro) return;

    const grupo = crear('g', { class: 'company-red-enlace' });

    // Un enlace fuerte es el del mismo techo: se pinta entero y más grueso, y
    // los demás con trazos. La diferencia importa más que el color, porque es
    // la que decide si dos saldos dependen del mismo dinero.
    const linea = crear('line', {
      stroke: enlace.fuerte ? color('--company-primary', '#1a4d8f') : color('--company-secondary', '#8a94a6'),
      'stroke-width': enlace.fuerte ? 2.5 : 1.5,
      'stroke-dasharray': enlace.fuerte ? null : '6 5',
    });

    grupo.appendChild(linea);

    let texto = null;

    if (enlace.etiqueta) {
      texto = crear('text', { class: 'company-red-etiqueta', 'text-anchor': 'middle' });
      texto.textContent = enlace.etiqueta;
      grupo.appendChild(texto);
    }

    this.capaEnlaces.appendChild(grupo);

    enlace.__pintado = { uno, otro, linea, texto };

    this.situarEnlace(enlace);
  }

  situarEnlace(enlace) {
    const pintado = enlace.__pintado;

    if (!pintado) return;

    const { uno, otro, linea, texto } = pintado;

    linea.setAttribute('x1', uno.x);
    linea.setAttribute('y1', uno.y);
    linea.setAttribute('x2', otro.x);
    linea.setAttribute('y2', otro.y);

    if (texto) {
      texto.setAttribute('x', (uno.x + otro.x) / 2);
      // Un poco por encima de la línea, no encima: el texto partido por un
      // trazo no se lee.
      texto.setAttribute('y', (uno.y + otro.y) / 2 - 6);
    }
  }

  pintarNodo(nodo) {
    // Con enlace es un enlace de verdad —se abre en pestaña nueva, se copia la
    // dirección—, y sin él un grupo suelto. Un `div` con un `click` encima no es
    // ninguna de las dos cosas.
    const grupo = crear(nodo.url ? 'a' : 'g', { class: 'company-red-nodo' });

    if (nodo.url) {
      grupo.setAttributeNS('http://www.w3.org/1999/xlink', 'href', nodo.url);
      grupo.setAttribute('href', nodo.url);
    }

    const burbuja = crear('circle', {
      r: RADIO,
      class: nodo.marcado ? 'company-red-burbuja company-red-burbuja-marcada' : 'company-red-burbuja',
    });

    grupo.appendChild(burbuja);

    // La inicial dentro, que sale siempre y no deja la burbuja vacía. Con foto
    // se pinta debajo de ella: si la imagen no llega —una dirección caducada,
    // el archivo borrado— queda la letra y no un agujero.
    const inicial = crear('text', {
      class: 'company-red-inicial',
      'text-anchor': 'middle',
      dy: '0.35em',
    });

    inicial.textContent = (nodo.nombre || '?').trim().charAt(0).toUpperCase();
    grupo.appendChild(inicial);

    let foto = null;

    if (nodo.foto) {
      // Recortada en redondo con un `clipPath` propio. Cada burbuja lleva el
      // suyo con su identificador porque un recorte se referencia por nombre y
      // dos redes en la misma página compartirían el del primero.
      const recorte = crear('clipPath', { id: `company-red-foto-${this.id}-${nodo.indice}` });
      const circulo = crear('circle', { r: RADIO });

      recorte.appendChild(circulo);
      grupo.appendChild(recorte);

      foto = crear('image', {
        class: 'company-red-foto',
        width: RADIO * 2,
        height: RADIO * 2,
        'clip-path': `url(#${recorte.id})`,
        // La foto de un carné no es cuadrada y encogerla lo sería: se recorta
        // por el lado que sobre, como en cualquier avatar.
        preserveAspectRatio: 'xMidYMid slice',
      });

      foto.setAttributeNS('http://www.w3.org/1999/xlink', 'href', nodo.foto);
      foto.setAttribute('href', nodo.foto);

      // Si no carga se quita y queda la inicial de debajo, que ya está puesta.
      foto.addEventListener('error', () => foto.remove());

      grupo.appendChild(foto);

      // El borde de la burbuja, otra vez encima: la foto lo tapaba y el aro
      // rojo de quien debe es la mitad de lo que se viene a mirar aquí.
      const aro = crear('circle', {
        r: RADIO,
        class: nodo.marcado ? 'company-red-aro company-red-aro-marcado' : 'company-red-aro',
      });

      grupo.appendChild(aro);

      nodo.__extras = { foto, aro, recorte: circulo };
    }

    const nombre = crear('text', {
      class: 'company-red-nombre',
      'text-anchor': 'middle',
      dy: RADIO + 18,
    });

    nombre.textContent = nodo.nombre || '';
    grupo.appendChild(nombre);

    let nota = null;

    if (nodo.nota) {
      nota = crear('text', {
        class: nodo.marcado ? 'company-red-nota company-red-nota-marcada' : 'company-red-nota',
        'text-anchor': 'middle',
        dy: RADIO + 34,
      });

      nota.textContent = nodo.nota;
      grupo.appendChild(nota);
    }

    if (nodo.detalle) {
      const titulo = crear('title');

      titulo.textContent = nodo.detalle;
      grupo.appendChild(titulo);
    }

    this.capaNodos.appendChild(grupo);

    nodo.__pintado = { grupo, burbuja, inicial, nombre, nota };

    this.situarNodo(nodo);
  }

  situarNodo(nodo) {
    const { burbuja, inicial, nombre, nota } = nodo.__pintado;

    burbuja.setAttribute('cx', nodo.x);
    burbuja.setAttribute('cy', nodo.y);
    inicial.setAttribute('x', nodo.x);
    inicial.setAttribute('y', nodo.y);
    nombre.setAttribute('x', nodo.x);
    nombre.setAttribute('y', nodo.y);

    if (nota) {
      nota.setAttribute('x', nodo.x);
      nota.setAttribute('y', nodo.y);
    }

    if (!nodo.__extras) return;

    const { foto, aro, recorte } = nodo.__extras;

    // La imagen se sitúa por su esquina y el recorte por su centro, así que la
    // esquina va medio diámetro arriba y a la izquierda.
    foto.setAttribute('x', nodo.x - RADIO);
    foto.setAttribute('y', nodo.y - RADIO);
    recorte.setAttribute('cx', nodo.x);
    recorte.setAttribute('cy', nodo.y);
    aro.setAttribute('cx', nodo.x);
    aro.setAttribute('cy', nodo.y);
  }

  /**
   * Arrastrar: la burbuja si se agarra una, y el lienzo entero si se agarra el
   * fondo.
   *
   * Son el mismo gesto sobre dos cosas distintas y así se espera que sea: se
   * empuja una persona para deshacer un cruce, y se empuja el fondo para mirar
   * la parte de la familia que se salió por el borde.
   *
   * Al mover una burbuja solo se mueve esa. Recalcular el resto mientras alguien
   * tira de una haría bailar justo lo que estaba mirando, y quien arrastra lo
   * hace precisamente porque quiere que lo demás se quede quieto.
   */
  permitirArrastre() {
    let arrastrando = null;
    let paseando = null;
    let movido = false;

    this.svg.addEventListener('pointerdown', (evento) => {
      const punto = this.enElDibujo(evento);
      const grupo = evento.target.closest('.company-red-nodo');

      movido = false;
      this.svg.setPointerCapture(evento.pointerId);

      if (grupo) {
        arrastrando = this.nodos.find((nodo) => nodo.__pintado.grupo === grupo) || null;
      }

      if (!arrastrando) {
        paseando = { x: punto.pantallaX - this.vista.x, y: punto.pantallaY - this.vista.y };
      }

      this.svg.classList.add('company-red-arrastrando');
    });

    this.svg.addEventListener('pointermove', (evento) => {
      if (!arrastrando && !paseando) return;

      const punto = this.enElDibujo(evento);

      movido = true;
      this.tocada = true;

      if (arrastrando) {
        arrastrando.x = punto.x;
        arrastrando.y = punto.y;

        this.situarNodo(arrastrando);
        this.enlaces.forEach((enlace) => this.situarEnlace(enlace));

        return;
      }

      this.vista.x = punto.pantallaX - paseando.x;
      this.vista.y = punto.pantallaY - paseando.y;

      this.aplicarVista();
    });

    const soltar = () => {
      arrastrando = null;
      paseando = null;
      this.svg.classList.remove('company-red-arrastrando');
    };

    this.svg.addEventListener('pointerup', soltar);
    this.svg.addEventListener('pointercancel', soltar);

    // Arrastrar una burbuja que además es un enlace no puede acabar abriendo la
    // ficha: quien la mueve no está pidiendo salir de esta pantalla.
    this.svg.addEventListener('click', (evento) => {
      if (movido) {
        evento.preventDefault();
        movido = false;
      }
    });

    // La rueda acerca y aleja en vez de mover la página. `passive: false` es
    // obligatorio: sin él el navegador ignora el `preventDefault` y la página
    // se desplaza por debajo mientras uno intenta acercar.
    this.svg.addEventListener(
      'wheel',
      (evento) => {
        evento.preventDefault();

        const punto = this.enElDibujo(evento);

        this.acercar(evento.deltaY < 0 ? PASO_DE_ZOOM : 1 / PASO_DE_ZOOM, punto.pantallaX, punto.pantallaY);
      },
      { passive: false },
    );
  }
}

export function montarRedes(raiz = document) {
  const ambito = raiz && typeof raiz.querySelectorAll === 'function' ? raiz : document;

  ambito.querySelectorAll(`[${ATTR}]`).forEach((elemento) => {
    if (!elemento.__companyRed) new Red(elemento);
  });
}

/**
 * La red montada sobre un elemento, para poder rehacerla desde fuera.
 *
 * @returns {?Red}
 */
export function redDe(elemento) {
  return elemento ? elemento.__companyRed || null : null;
}
